import type { BrowserFilterConfig } from '@/components/ObjectBrowser';
import {
  INITIATIVE_TYPE_CONFIG,
  INITIATIVE_TAG_OPTIONS,
  INITIATIVE_OCCASION_OPTIONS,
  INITIATIVE_PUBLIC_STATUSES,
} from '@sizzlenz/shared';
import type { InitiativesQuery } from '@/lib/api/types';

/**
 * Shared filter configurations for what's on / initiative views.
 * Used by /pages/opportunities and HubView WhatsOnTabContent.
 */
export const WHATSON_FILTER_CONFIGS: BrowserFilterConfig[] = [
  {
    key: 'categories',
    label: 'Aimed at',
    options: [
      { id: 'trade', label: 'Trade' },
      { id: 'consumer', label: 'Consumer' },
    ],
  },
  {
    key: 'type',
    label: 'Type',
    options: Object.entries(INITIATIVE_TYPE_CONFIG)
      .filter(([key]) => key !== 'quiz')
      .map(([key, config]) => ({ id: key, label: config.label })),
    displayMode: 'combobox',
  },
  {
    key: 'activities',
    label: 'Activities',
    options: INITIATIVE_TAG_OPTIONS.filter((a) => a.scopes?.includes('activity')).map((a) => ({
      id: a.id,
      label: a.label,
    })),
    displayMode: 'combobox',
  },
  {
    key: 'occasions',
    label: 'Occasion',
    options: INITIATIVE_OCCASION_OPTIONS.map((o) => ({
      id: o.id,
      label: o.label,
    })),
    displayMode: 'combobox',
  },
];

/** Admin-only status filter config */
export const WHATSON_STATUS_FILTER: BrowserFilterConfig = {
  key: 'status',
  label: 'Status',
  options: [
    { id: 'active', label: 'Active' },
    { id: 'closed', label: 'Closed' },
  ],
};

/** Get filter configs, optionally including admin status filter */
export function getWhatsOnFilterConfigs(isAdmin: boolean): BrowserFilterConfig[] {
  if (!isAdmin) return WHATSON_FILTER_CONFIGS;
  return [...WHATSON_FILTER_CONFIGS];
}

/** URL sync filter definitions for what's on views */
export const WHATSON_URL_SYNC_FILTERS = {
  search: { paramName: 'search', defaultValue: '' },
  categories: {
    paramName: 'categories',
    defaultValue: [] as string[],
    isArray: true as const,
  },
  type: { paramName: 'type', defaultValue: [] as string[], isArray: true as const },
  activities: { paramName: 'activities', defaultValue: [] as string[], isArray: true as const },
  occasions: { paramName: 'occasions', defaultValue: [] as string[], isArray: true as const },
  status: { paramName: 'status', defaultValue: [] as string[], isArray: true as const },
  tags: { paramName: 'tags', defaultValue: [] as string[], isArray: true as const },
  dateFrom: { paramName: 'dateFrom', defaultValue: '' },
  dateTo: { paramName: 'dateTo', defaultValue: '' },
  interested: { paramName: 'interested', defaultValue: '' },
  assigned: { paramName: 'assigned', defaultValue: '' },
};

/** Build initialFilters record from URL sync values */
export function buildWhatsOnInitialFilters(
  values: {
    categories: string[];
    type: string[];
    activities: string[];
    occasions: string[];
    status: string[];
  },
  isAdmin: boolean
): Record<string, string[]> {
  const f: Record<string, string[]> = {};
  if (values.categories.length > 0) f.categories = values.categories;
  if (values.type.length > 0) f.type = values.type;
  if (values.activities.length > 0) f.activities = values.activities;
  if (values.occasions.length > 0) f.occasions = values.occasions;
  if (isAdmin && values.status.length > 0) f.status = values.status;
  return f;
}

/** Sync filter changes back to URL handlers */
export function syncWhatsOnFilters(
  filters: Record<string, string[]>,
  handlers: {
    categories: (v: string[]) => void;
    type: (v: string[]) => void;
    activities: (v: string[]) => void;
    occasions: (v: string[]) => void;
    status: (v: string[]) => void;
  },
  isAdmin: boolean
) {
  handlers.categories(filters.categories || []);
  handlers.type(filters.type || []);
  handlers.activities(filters.activities || []);
  handlers.occasions(filters.occasions || []);
  if (isAdmin) {
    handlers.status(filters.status || []);
  }
}

/** Build initiative API query params from browser filter state */
export function buildWhatsOnApiQuery(params: {
  search?: string;
  filters: Record<string, string[]>;
  cursor?: string;
  limit: number;
  dateFrom?: string;
  dateTo?: string;
  categories?: string[];
  tags?: string[];
  tagExpression?: Record<string, any>;
  requiredSystemTags?: string[];
  optionalSystemTags?: string[];
  dateRequired?: boolean;
  includePast?: boolean;
  interested?: boolean;
  assigned?: boolean;
}): InitiativesQuery {
  const types = params.filters.type || [];
  const activities = params.filters.activities || [];
  const occasions = params.filters.occasions || [];
  const statusArr = params.filters.status || [];
  const filterCategories = params.filters.categories || [];

  // Use filter-selected categories if present, otherwise fall back to explicit param
  const resolvedCategories = filterCategories.length > 0 ? filterCategories : params.categories;

  const query: InitiativesQuery = {
    search: params.search,
    type: types.length > 0 ? types : undefined,
    activities: activities.length > 0 ? activities : undefined,
    occasions: occasions.length > 0 ? occasions : undefined,
    status: statusArr.length > 0 ? statusArr : [...INITIATIVE_PUBLIC_STATUSES],
    visibility: 'public',
    isTemplate: false,
    dateFrom: params.dateFrom || undefined,
    dateTo: params.dateTo || undefined,
    dateRequired: params.dateRequired,
    includePast: params.includePast,
    limit: params.limit,
    categories: resolvedCategories,
    nextCursor: params.cursor || undefined,
  };

  if (params.tags) {
    (query as any).tags = params.tags;
  }

  if (params.requiredSystemTags) {
    (query as any).requiredSystemTags = params.requiredSystemTags;
  }

  if (params.optionalSystemTags) {
    (query as any).optionalSystemTags = params.optionalSystemTags;
  }

  if (params.tagExpression) {
    (query as any).tagExpression = params.tagExpression;
  }

  if (params.interested) {
    query.interestedUserId = 'true';
  }

  if (params.assigned) {
    query.assignedUserId = 'true';
  }

  return query;
}
